import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Network, Search, CheckCircle, AlertCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'compliant':
      return <CheckCircle size={14} className="text-emerald-500" />;
    case 'gap':
      return <AlertCircle size={14} className="text-red-500" />;
    default:
      return <Clock size={14} className="text-amber-500" />;
  }
};

const FrameworkBadge = ({ name }) => {
  const colors = {
    'SOC 2': 'bg-blue-100 text-blue-700 border-blue-200',
    'ISO 27001': 'bg-purple-100 text-purple-700 border-purple-200',
    'GDPR': 'bg-green-100 text-green-700 border-green-200',
    'HIPAA': 'bg-orange-100 text-orange-700 border-orange-200'
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[name] || 'bg-gray-100 text-gray-700'}`}>
      {name}
    </span>
  );
};

export default function ControlMappingPage() {
  const [ccfMappings, setCcfMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchCCFMappings();
  }, []);

  const fetchCCFMappings = async () => {
    try {
      const response = await api.get('/control-mappings/ccf');
      setCcfMappings(response.data.mappings || []);
      // Expand all by default
      const expanded = {};
      response.data.mappings?.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load control mappings');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (idx) => {
    setExpandedCategories(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const filteredMappings = ccfMappings.filter(mapping =>
    mapping.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mapping.frameworks.some(fw => 
      fw.control_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fw.framework.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Calculate overall compliance for CCF
  const totalControls = ccfMappings.reduce((sum, m) => sum + m.frameworks.length, 0);
  const compliantControls = ccfMappings.reduce((sum, m) => 
    sum + m.frameworks.filter(f => f.compliance_status === 'compliant').length, 0);
  const complianceRate = totalControls > 0 ? Math.round((compliantControls / totalControls) * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Control Mapping"
        subtitle="Map Once, Comply Many - Cross-framework control relationships"
        icon={<Network size={28} />}
      />

      {/* CCF Overview */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-sm p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-2">Common Control Framework (CCF)</h2>
            <p className="text-indigo-100 text-sm max-w-2xl">
              The CCF maps equivalent controls across SOC 2, ISO 27001, GDPR, and HIPAA. 
              Implement one control to satisfy requirements in multiple frameworks simultaneously.
            </p>
          </div>
          <div className="text-center bg-white/10 rounded-sm px-6 py-4">
            <div className="text-4xl font-bold">{complianceRate}%</div>
            <div className="text-sm text-indigo-200">CCF Compliance</div>
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold">{ccfMappings.length}</div>
            <div className="text-xs text-indigo-200">Control Categories</div>
          </div>
          <div className="bg-white/10 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold">4</div>
            <div className="text-xs text-indigo-200">Frameworks Mapped</div>
          </div>
          <div className="bg-white/10 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold">{compliantControls}</div>
            <div className="text-xs text-indigo-200">Compliant Controls</div>
          </div>
          <div className="bg-white/10 rounded-sm p-3 text-center">
            <div className="text-2xl font-bold">{totalControls - compliantControls}</div>
            <div className="text-xs text-indigo-200">Remaining</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="ccf-search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by category, framework, or control code..."
          className="pl-10 rounded-sm"
        />
      </div>

      {/* CCF Mappings */}
      <div className="space-y-4">
        {filteredMappings.map((mapping, idx) => (
          <div key={idx} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <button
              onClick={() => toggleCategory(idx)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              data-testid={`ccf-category-${idx}`}
            >
              <div className="flex items-center gap-3">
                {expandedCategories[idx] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <span className="font-semibold text-lg">{mapping.category}</span>
                <span className="text-sm text-slate-500">
                  ({mapping.frameworks.filter(f => f.compliance_status === 'compliant').length}/{mapping.frameworks.length} compliant)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {mapping.frameworks.map((fw, fwIdx) => (
                  <FrameworkBadge key={fwIdx} name={fw.framework} />
                ))}
              </div>
            </button>
            
            {expandedCategories[idx] && (
              <div className="border-t border-slate-200 p-4">
                <div className="grid grid-cols-4 gap-4">
                  {mapping.frameworks.map((fw, fwIdx) => (
                    <div 
                      key={fwIdx} 
                      className={`p-4 rounded-sm border ${
                        fw.compliance_status === 'compliant' 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : fw.compliance_status === 'gap'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <FrameworkBadge name={fw.framework} />
                        <StatusIcon status={fw.compliance_status} />
                      </div>
                      <div className="font-mono text-sm font-semibold text-slate-700 mb-1">
                        {fw.control_code}
                      </div>
                      <div className="text-xs text-slate-500 line-clamp-2">
                        {fw.control_title || 'Control details'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredMappings.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-sm">
          <Network size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No control mappings found matching your search.</p>
        </div>
      )}
    </DashboardLayout>
  );
}
